import { Injectable } from "@angular/core";
import { IndexdbService } from "./indexdb-service";

import { Inject, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";

@Injectable({
  providedIn: "root",
})
export class CryptoService {
  isPlateform: boolean;
  constructor(
    private indexdbService: IndexdbService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isPlateform = isPlatformBrowser(this.platformId);
  }

  async importAesKeyFromBase64(base64Key: string): Promise<CryptoKey> {
    // Convert base64 → Uint8Array
    const raw = Uint8Array.from(atob(base64Key), (c) => c.charCodeAt(0));

    // Import into SubtleCrypto as AES-GCM key
    return await crypto.subtle.importKey(
      "raw",
      raw.buffer, // ArrayBuffer
      { name: "AES-GCM" },
      true, // extractable (true if you want to export again later)
      ["encrypt", "decrypt"],
    );
  }

  /**
   * Generate a new AES-GCM key (256-bit).
   */
  async generateKey(): Promise<CryptoKey> {
    return crypto.subtle.generateKey(
      {
        name: "AES-GCM",
        length: 256,
      },
      true, // extractable = true so we can export it
      ["encrypt", "decrypt"],
    );
  }

  /**
   * Export AES key to raw (Uint8Array) -> Base64 (so you can send it to server).
   */
  async exportKey(key: CryptoKey): Promise<string> {
    const raw = await crypto.subtle.exportKey("raw", key);
    const buffer = new Uint8Array(raw);
    return btoa(String.fromCharCode(...buffer));
  }

  async exportAesKey(key: CryptoKey): Promise<Uint8Array> {
    const raw = await crypto.subtle.exportKey("raw", key);
    return new Uint8Array(raw);
  }

  async encrypt(key: CryptoKey, data: any, spk: string): Promise<string> {
    const encoded = new TextEncoder().encode(
      typeof data == "string" ? data : JSON.stringify(data),
    );

    // Generate 96-bit IV (recommended for GCM)
    //const iv = crypto.getRandomValues(new Uint8Array(12));
    const iv = crypto.getRandomValues(new Uint8Array(12));

    console.log("iv: ", this.arrayBufferToBase64(iv));

    // Encrypt -> ciphertext + tag appended
    const cipherBuffer = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      encoded.buffer, // ✅ Use .buffer instead of passing Uint8Array
    );

    const fullBytes = new Uint8Array(cipherBuffer);
    const tagLength = 16; // 128-bit tag
    const cipherBytes = fullBytes.slice(0, fullBytes.length - tagLength);
    const tagBytes = fullBytes.slice(fullBytes.length - tagLength);
    let serverRSA: any = this.base64ToUtf8(spk || "");

    console.log("Tag: ", this.arrayBufferToBase64(tagBytes));

    let encryptedTag = await this.encryptAesKeyWithRsa(tagBytes, serverRSA);

    return `${this.arrayBufferToBase64(cipherBytes)}|${encryptedTag}|${this.arrayBufferToBase64(iv)}`;
  }

  base64ToUtf8(base64: string): string {
    if (this.isPlateform) {
      return decodeURIComponent(escape(window.atob(base64)));
    } else {
      return Buffer.from(base64, "base64").toString("utf-8");
    }
  }

  /**
   * Utility: ArrayBuffer → Base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
    const bytes =
      buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Decrypt data with AES-GCM.
   */
  async decrypt(
    key: CryptoKey,
    cipherText: string,
    iv: string,
  ): Promise<string> {
    const dec = new TextDecoder();

    const cipherBytes = Uint8Array.from(cipherText, (c) => c.charCodeAt(0));
    const ivBytes = Uint8Array.from(iv, (c) => c.charCodeAt(0));

    const plainBuffer = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: ivBytes },
      key,
      cipherBytes,
    );

    return dec.decode(plainBuffer);
  }

  async decryptFromDotNet(
    encryptedString: string,
    key: CryptoKey,
    cpk,
  ): Promise<string> {
    //console.log("Encrypted String:", encryptedString);
    const parts = encryptedString?.split("|");
    if (parts?.length !== 3) {
      throw new Error("Invalid encrypted payload");
    }

    let tag = parts[1];

    //let cpk = localStorage.getItem('cpk') ?? '';

    let dAes = await this.decryptAesKeyWithRsa(this.base64ToUtf8(cpk), tag);

    const cipherBytes = Uint8Array.from(atob(parts[0]), (c) => c.charCodeAt(0));
    const tagBytes = Uint8Array.from(atob(dAes), (c) => c.charCodeAt(0));
    const nonceBytes = Uint8Array.from(atob(parts[2]), (c) => c.charCodeAt(0));

    // In WebCrypto, AES-GCM expects ciphertext **with the tag appended**
    const cipherWithTag = new Uint8Array(cipherBytes.length + tagBytes.length);
    cipherWithTag.set(cipherBytes);
    cipherWithTag.set(tagBytes, cipherBytes.length);

    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: nonceBytes,
      },
      key,
      cipherWithTag,
    );

    return new TextDecoder().decode(new Uint8Array(decryptedBuffer));
  }

  /**
   * Import RSA public key from PEM string
   */
  private async importRsaPublicKey(pem: string): Promise<CryptoKey> {
    // Clean PEM
    const pemHeader = "-----BEGIN PUBLIC KEY-----";
    const pemFooter = "-----END PUBLIC KEY-----";
    const pemContents = pem
      .replace(pemHeader, "")
      .replace(pemFooter, "")
      .replace(/\s/g, "");

    const binaryDer = Uint8Array.from(atob(pemContents), (c) =>
      c.charCodeAt(0),
    );

    return crypto.subtle.importKey(
      "spki",
      binaryDer.buffer,
      { name: "RSA-OAEP", hash: "SHA-256" },
      true,
      ["encrypt"],
    );
  }

  /**
   * Encrypt AES key with RSA public key (RSA-OAEP)
   */
  async encryptAesKeyWithRsa(
    aesRaw: any,
    rsaPublicKeyPem: string,
  ): Promise<string> {
    // Import RSA public key from PEM
    const rsaKey = await this.importRsaPublicKey(rsaPublicKeyPem);

    // Export AES key as raw bytes
    //const aesRaw = await crypto.subtle.exportKey("raw", aesKey);

    // Encrypt the AES key using RSA-OAEP
    const encrypted = await crypto.subtle.encrypt(
      { name: "RSA-OAEP" },
      rsaKey,
      aesRaw,
    );

    // Convert ArrayBuffer → Base64
    //return this.arrayBufferToBase64(encrypted);
    // Convert ArrayBuffer → Base64
    return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
  }

  
  async decryptAesKeyWithRsa(
    privatePem: string,
    encryptedBase64: string,
  ): Promise<string> {
    // --- Step 1: Clean PEM ---
    const pemHeader = "-----BEGIN PRIVATE KEY-----";
    const pemFooter = "-----END PRIVATE KEY-----";
    const pemContents = privatePem
      .replace(pemHeader, "")
      .replace(pemFooter, "")
      .replace(/\s/g, "");

    const binaryDer = Uint8Array.from(atob(pemContents), (c) =>
      c.charCodeAt(0),
    );

    // --- Step 2: Import RSA Private Key (PKCS8) ---
    const privateKey = await crypto.subtle.importKey(
      "pkcs8",
      binaryDer.buffer,
      { name: "RSA-OAEP", hash: "SHA-256" },
      false,
      ["decrypt"],
    );

    // --- Step 3: Decode Encrypted AES Key (Base64 → bytes) ---
    const encryptedBytes = Uint8Array.from(atob(encryptedBase64), (c) =>
      c.charCodeAt(0),
    );

    // --- Step 4: Decrypt with RSA-OAEP ---
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: "RSA-OAEP" },
      privateKey,
      encryptedBytes,
    );

    let decryptedBytes = new Uint8Array(decryptedBuffer);
    const decryptedString = new TextDecoder("utf-8").decode(decryptedBytes);

    // --- Step 5: Return as Uint8Array (raw AES key bytes) ---
    //console.log(decryptedString);
    return decryptedString;
  }
}
